/* =========================================================
   Notification bell + queue popover.

   Every system toast is replicated into this queue. The badge
   sits at the bell's lower-left corner; while unread items
   remain the bell vibrates every 30s and (when sound is on)
   chimes every 3 minutes. Clicking an item expands its origin,
   type, full timestamp and detail.
   ========================================================= */
import React from 'react';
import type { Notification, NotifKind } from '@/types';
import { ic, sized, type IconName } from '@/components/Icons';
import { ds } from '@/data';
import { useI18n } from '@/i18n';
import { notifySound } from '@/lib/sfx';
import { useAppStore } from '@/store/useAppStore';
import { useGo, type ScreenId } from '@/hooks/useRoute';

function timeAgo(ts: number, lang: 'es' | 'en') {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return lang === 'es' ? 'ahora' : 'now';
  const m = Math.floor(s / 60);
  if (m < 60) return lang === 'es' ? `hace ${m} min` : `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return lang === 'es' ? `hace ${h} h` : `${h}h ago`;
  const d = Math.floor(h / 24);
  return lang === 'es' ? `hace ${d} d` : `${d}d ago`;
}

const NOTIF_STYLE: Record<NotifKind, { color: string; bg: string; icon: IconName }> = {
  ok:     { color: 'var(--ok)',   bg: 'var(--ok-l)',     icon: 'check' },
  err:    { color: 'var(--err)',  bg: 'var(--st-req-l)', icon: 'warn' },
  delete: { color: 'var(--err)',  bg: 'var(--st-req-l)', icon: 'trash' },
  info:   { color: 'var(--info)', bg: '#E8F0FC',         icon: 'info' },
  warn:   { color: 'var(--warn)', bg: 'var(--st-edit-l)',icon: 'warn' },
};

export function NotifBell() {
  const { lang } = useI18n();
  const go = useGo();
  const [open, setOpen] = React.useState(false);
  const [expanded, setExpanded] = React.useState<string | null>(null);
  const [shaking, setShaking] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  // re-render whenever the queue mutates
  const version = useAppStore((s) => s.notifVersion);
  const bump = useAppStore((s) => s.bumpNotifications);
  void version;

  const list = ds.listNotifications();
  const unread = ds.unreadNotifications();

  // La cola viene del servidor: los avisos los genera OTRA persona, así
  // que hay que ir a buscarlos. Cada minuto mientras la pestaña esté a la
  // vista —en segundo plano no hay a quién avisar— y siempre al abrirla,
  // para que lo que se lee sea lo de ahora y no lo del inicio de sesión.
  const releer = React.useCallback(() => {
    void ds.refreshNotifications().then(bump).catch(() => {
      /* sin conexión se sigue viendo lo último que llegó */
    });
  }, [bump]);

  React.useEffect(() => {
    releer();
    const iv = window.setInterval(() => {
      if (document.visibilityState === 'visible') releer();
    }, 60000);
    const alVolver = () => {
      if (document.visibilityState === 'visible') releer();
    };
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      window.clearInterval(iv);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [releer]);

  React.useEffect(() => {
    const shakeIv = window.setInterval(() => {
      if (ds.unreadNotifications() > 0) {
        setShaking(true);
        window.setTimeout(() => setShaking(false), 850);
      }
    }, 30000);
    const soundIv = window.setInterval(() => {
      if (ds.unreadNotifications() > 0) notifySound();
    }, 180000);
    return () => {
      window.clearInterval(shakeIv);
      window.clearInterval(soundIv);
    };
  }, []);

  const close = React.useCallback(() => {
    setOpen(false);
    setExpanded(null);
    ds.markAllNotificationsRead();
    bump();
  }, [bump]);

  React.useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, close]);

  function toggleItem(n: Notification) {
    if (!n.read) {
      n.read = true;
      ds.saveNotifications();
    }
    setExpanded((x) => (x === n.id ? null : n.id));
    bump();
  }

  const KIND_LABEL: Record<NotifKind, string> = {
    ok:     lang === 'es' ? 'Confirmación' : 'Confirmation',
    err:    lang === 'es' ? 'Error' : 'Error',
    delete: lang === 'es' ? 'Baja / eliminación' : 'Removal',
    info:   lang === 'es' ? 'Aviso' : 'Notice',
    warn:   lang === 'es' ? 'Pendiente' : 'Pending',
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="btn icon ghost"
        data-tip-pos="below"
        data-tip={lang === 'es' ? 'Notificaciones' : 'Notifications'}
        onClick={() => (open ? close() : (setOpen(true), releer()))}
        style={{ position: 'relative' }}
        aria-label={lang === 'es' ? 'Notificaciones' : 'Notifications'}
      >
        <span style={{ display: 'inline-flex', transformOrigin: 'top center', animation: shaking ? 'bellShake .85s ease' : 'none' }}>
          {ic.bell}
        </span>
        {unread > 0 ? (
          <span
            className="mono"
            style={{
              position: 'absolute', bottom: 2, left: 2, minWidth: 11, height: 11, padding: '0 2.5px',
              borderRadius: 99, background: 'var(--err)', color: '#fff', fontSize: 8, fontWeight: 800,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
              border: '1.5px solid var(--surface)', boxSizing: 'content-box',
            }}
          >
            {unread > 9 ? '9+' : unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          className="card"
          style={{
            position: 'absolute', right: 0, top: 'calc(100% + 8px)', width: 350, maxWidth: 'calc(100vw - 24px)',
            zIndex: 1200, boxShadow: 'var(--shadow-3)', overflow: 'hidden', animation: 'siteCardIn .12s ease both',
          }}
        >
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: 'var(--surface-2)' }}>
            <div style={{ fontWeight: 800, fontSize: 13 }}>{lang === 'es' ? 'Notificaciones' : 'Notifications'}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {unread > 0 ? <span className="chip err" style={{ fontSize: 9 }}>{unread} {lang === 'es' ? 'nuevas' : 'new'}</span> : null}
              {list.length > 0 ? (
                <button className="btn sm ghost" onClick={() => { ds.clearNotifications(); bump(); }} data-tip={lang === 'es' ? 'Vaciar cola' : 'Clear queue'}>
                  {sized(ic.trash, 12)}
                </button>
              ) : null}
            </div>
          </div>

          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {list.length === 0 ? (
              <div style={{ padding: '28px 16px', textAlign: 'center', color: 'var(--text-3)' }}>
                <div style={{ display: 'inline-flex', marginBottom: 8, color: 'var(--line-2)' }}>{sized(ic.bell, 26)}</div>
                <div style={{ fontSize: 12.5 }}>{lang === 'es' ? 'Sin notificaciones en la cola.' : 'No notifications in the queue.'}</div>
              </div>
            ) : (
              list.map((n) => {
                const st = NOTIF_STYLE[n.kind as NotifKind] || NOTIF_STYLE.info;
                const isOpen = expanded === n.id;
                return (
                  <div key={n.id}>
                    <button
                      onClick={() => toggleItem(n)}
                      style={{
                        display: 'flex', gap: 10, padding: '10px 14px',
                        borderBottom: isOpen ? 'none' : '1px solid var(--line)',
                        background: n.read ? 'transparent' : 'var(--brand-primary-l)',
                        alignItems: 'flex-start', width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
                      }}
                    >
                      <span style={{ width: 26, height: 26, borderRadius: 99, background: st.bg, color: st.color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                        {sized(ic[st.icon], 13)}
                      </span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 12.5, fontWeight: n.read ? 600 : 700, lineHeight: 1.35, color: 'var(--text)' }}>{n.title}</div>
                        {n.body ? <div style={{ fontSize: 11.5, color: 'var(--text-2)', marginTop: 2, lineHeight: 1.4 }}>{n.body}</div> : null}
                        <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 4 }}>{timeAgo(n.ts, lang)}</div>
                      </div>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0, marginTop: 4 }}>
                        {!n.read ? <span style={{ width: 7, height: 7, borderRadius: 99, background: 'var(--brand-primary)' }} /> : null}
                        <span style={{ color: 'var(--text-3)', display: 'inline-flex', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s ease' }}>
                          {sized(ic.chevD, 13)}
                        </span>
                      </span>
                    </button>

                    {isOpen ? (
                      <div style={{ padding: '2px 14px 12px 50px', borderBottom: '1px solid var(--line)', background: n.read ? 'transparent' : 'var(--brand-primary-l)' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11.5, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 8, padding: '9px 11px' }}>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <span style={{ flex: '0 0 56px', color: 'var(--text-3)', fontWeight: 700 }}>{lang === 'es' ? 'Origen' : 'Origin'}</span>
                            <span style={{ fontWeight: 600 }}>{n.origin || 'ETC Connect'}</span>
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <span style={{ flex: '0 0 56px', color: 'var(--text-3)', fontWeight: 700 }}>{lang === 'es' ? 'Tipo' : 'Type'}</span>
                            <span>{KIND_LABEL[n.kind as NotifKind] || KIND_LABEL.info}</span>
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <span style={{ flex: '0 0 56px', color: 'var(--text-3)', fontWeight: 700 }}>{lang === 'es' ? 'Fecha' : 'Date'}</span>
                            <span className="mono" style={{ fontSize: 11 }}>
                              {new Date(n.ts).toLocaleString(lang === 'es' ? 'es-MX' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          {n.details ? (
                            <div style={{ display: 'flex', gap: 8 }}>
                              <span style={{ flex: '0 0 56px', color: 'var(--text-3)', fontWeight: 700 }}>{lang === 'es' ? 'Detalle' : 'Detail'}</span>
                              <span style={{ color: 'var(--text-2)' }}>{n.details}</span>
                            </div>
                          ) : null}
                        </div>
                        {n.target ? (
                          <button
                            className="btn sm primary"
                            style={{ marginTop: 8 }}
                            onClick={() => {
                              go(n.target!.screen as ScreenId, n.target!.params);
                              close();
                            }}
                          >
                            {ic.chevR} {n.targetLabel || (lang === 'es' ? 'Abrir sección' : 'Open section')}
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
